/* Momo — mascotte animée Morfylo (corps WebP + visage SVG).
   Usage : {% render 'momo', state: 'awake|sleepy|wave|thumb|dozing|still', size: 120 %}
   Le snippet pose <div data-momo data-size><img class="momo__body"></div> ; ce script ajoute le rig (corps +
   visage), l'ombre, les zzz et les comportements :
   · clignement, bâillement, respiration, regard qui suit le pointeur ET le doigt (touchmove)
   · entrée en scène quand la section arrive à l'écran (glisse depuis le bord + petit salut)
   · réaction au tap/clic : rebond squash + 6 étoiles qui jaillissent
   · « thumb » = salue avec le pouce levé · « dozing » = bâille → s'allonge → s'endort (zzz) quand il apparaît
   Performance : transform / opacity uniquement. Tout est coupé en prefers-reduced-motion.
   API : window.Momo.mount() · window.Momo.set(el, state) · window.Momo.yawn(el) · window.Momo.boing(el) */
(() => {
  'use strict';
  const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const css = `
  .momo{cursor:pointer;-webkit-tap-highlight-color:transparent}
  .momo__rig{position:absolute;inset:0;transform-origin:50% 62%;transition:transform 1.2s cubic-bezier(.16,1,.3,1)}
  .momo__body{position:absolute;inset:0;width:100%;height:100%;object-fit:contain;transform-origin:50% 100%}
  .momo__face{position:absolute;left:0;top:0;width:100%;height:100%;overflow:visible}
  .momo__eye{transform-box:fill-box;transform-origin:center}
  .momo__lid{opacity:0}
  .momo__yawn{opacity:0;transform-box:fill-box;transform-origin:center}
  .momo__thumb{opacity:0;transform-box:fill-box;transform-origin:50% 100%;transform:scale(.4) rotate(20deg);transition:opacity .4s,transform .6s cubic-bezier(.34,1.56,.64,1)}
  .momo--thumb .momo__thumb{opacity:1;transform:none}
  .momo__zz{position:absolute;left:66%;top:8%;font:700 18px Poppins,system-ui,sans-serif;color:#fff;text-shadow:0 2px 8px rgba(7,10,38,.5);pointer-events:none;transition:left .8s,top .8s}
  .momo__zz span{position:absolute;opacity:0;animation:momo-zz 3.6s ease-in-out infinite}
  .momo__zz span:nth-child(2){left:10px;top:-12px;font-size:22px;animation-delay:.6s}.momo__zz span:nth-child(3){left:22px;top:-26px;font-size:26px;animation-delay:1.2s}
  .momo__shadow{position:absolute;left:18%;right:18%;bottom:-4%;height:8%;border-radius:50%;background:radial-gradient(ellipse,rgba(7,10,38,.35),transparent 70%);transform-origin:center;transition:transform 1.2s cubic-bezier(.16,1,.3,1)}
  .momo:not(.momo--still) .momo__body{animation:momo-breathe 3.6s ease-in-out infinite}
  .momo:not(.momo--still) .momo__shadow{animation:momo-shadow 3.6s ease-in-out infinite}
  .momo--wave .momo__body,.momo--thumb .momo__body{animation:momo-breathe 3.6s ease-in-out infinite,momo-bob 2.8s ease-in-out infinite}
  .momo--sleepy .momo__lid{opacity:1}.momo--sleepy .momo__eye,.momo--sleepy .momo__hl{opacity:0}.momo--sleepy .momo__mouth{d:path("M-5 0 Q0 3 5 0")}
  .momo.is-blink .momo__eye{animation:momo-blink .22s ease-in-out}
  .momo.is-yawn .momo__yawn{opacity:1;animation:momo-yawn 1.6s ease-in-out}.momo.is-yawn .momo__mouth{opacity:0}
  .momo.is-enter{animation:momo-enter 1.1s cubic-bezier(.16,1,.3,1) both}
  .momo.is-boing{animation:momo-boing .7s cubic-bezier(.34,1.56,.64,1)}
  .momo.is-boing .momo__body{animation:none}
  .momo.is-lying .momo__rig{transform:rotate(-80deg) translate(-6%,4%)}
  .momo.is-lying .momo__shadow{transform:scaleX(1.45)}
  .momo.is-lying .momo__zz{left:2%;top:10%}
  .momo__star{position:absolute;left:50%;top:40%;width:14px;height:14px;fill:#FFD9A3;pointer-events:none;z-index:3;filter:drop-shadow(0 0 5px rgba(255,217,163,.9));animation:momo-star var(--dur,.9s) cubic-bezier(.16,1,.3,1) forwards}
  @keyframes momo-breathe{0%,100%{transform:scale(1,1)}50%{transform:scale(1.025,.975)}}
  @keyframes momo-shadow{0%,100%{transform:scaleX(1);opacity:1}50%{transform:scaleX(1.06);opacity:.85}}
  @keyframes momo-bob{0%,100%{translate:0 0;rotate:-2deg}50%{translate:0 -6px;rotate:2deg}}
  @keyframes momo-blink{0%,100%{transform:scaleY(1)}50%{transform:scaleY(.08)}}
  @keyframes momo-yawn{0%{transform:scale(.2)}30%{transform:scale(1.1)}70%{transform:scale(1)}100%{transform:scale(.2)}}
  @keyframes momo-zz{0%{opacity:0;transform:translateY(6px)}25%{opacity:1}70%{opacity:1}100%{opacity:0;transform:translateY(-14px)}}
  @keyframes momo-enter{0%{opacity:0;transform:translate3d(var(--from,70px),12px,0) rotate(var(--rot,10deg)) scale(.9)}55%{opacity:1;transform:translate3d(-6px,-6px,0) rotate(-5deg) scale(1.03)}78%{transform:translate3d(3px,0,0) rotate(3deg)}100%{opacity:1;transform:none}}
  @keyframes momo-boing{0%{transform:scale(1,1)}25%{transform:scale(1.18,.8) translateY(6%)}50%{transform:scale(.9,1.14) translateY(-8%)}70%{transform:scale(1.06,.95)}100%{transform:none}}
  @keyframes momo-star{0%{opacity:1;transform:translate(-50%,-50%) scale(.3) rotate(0)}100%{opacity:0;transform:translate(calc(-50% + var(--dx)),calc(-50% + var(--dy))) scale(1.4) rotate(120deg)}}
  @media (prefers-reduced-motion:reduce){.momo *,.momo{animation:none!important;transition:none!important}}`;
  const style = document.createElement('style');
  style.textContent = css;
  document.head.appendChild(style);

  // Visage en coordonnées du corps (588×640) : yeux à ~ (222,262) et (338,262), joues, bouche à (280,304), main levée ~ (536,292)
  const FACE = `<svg class="momo__face" viewBox="0 0 588 640" aria-hidden="true" focusable="false">
    <g transform="translate(222 262)"><ellipse class="momo__eye" rx="13" ry="16" fill="#3B2A2A"/><circle class="momo__hl" cx="-4" cy="-5" r="4" fill="#fff"/><path class="momo__lid" d="M-16 2 Q0 12 16 2" fill="none" stroke="#3B2A2A" stroke-width="5" stroke-linecap="round"/></g>
    <g transform="translate(338 262)"><ellipse class="momo__eye" rx="13" ry="16" fill="#3B2A2A"/><circle class="momo__hl" cx="-4" cy="-5" r="4" fill="#fff"/><path class="momo__lid" d="M-16 2 Q0 12 16 2" fill="none" stroke="#3B2A2A" stroke-width="5" stroke-linecap="round"/></g>
    <ellipse cx="188" cy="300" rx="22" ry="12" fill="#F6A8B4" opacity=".9"/>
    <ellipse cx="372" cy="300" rx="22" ry="12" fill="#F6A8B4" opacity=".9"/>
    <g transform="translate(280 304)"><path class="momo__mouth" d="M-10 0 Q0 9 10 0" fill="none" stroke="#5A2F2A" stroke-width="5" stroke-linecap="round"/><ellipse class="momo__yawn" rx="13" ry="17" fill="#6B2E30"/></g>
    <g transform="translate(540 284)"><g class="momo__thumb"><path d="M-28 8h44a18 18 0 0 1 0 36h-44z" fill="#FFF3E6" stroke="#8C5F48" stroke-width="6" stroke-linejoin="round"/><path d="M-6 8V-24a13 13 0 0 1 26 0V8" fill="#FFF3E6" stroke="#8C5F48" stroke-width="6" stroke-linejoin="round"/></g></g>
  </svg>`;
  const STAR = '<svg class="momo__star" viewBox="0 0 24 24" aria-hidden="true"><path d="M12 0c1 7 5 11 12 12-7 1-11 5-12 12-1-7-5-11-12-12 7-1 11-5 12-12z"/></svg>';
  const STATES = ['awake', 'sleepy', 'wave', 'thumb', 'dozing', 'still'];

  /* ---------- regard : un seul écouteur pointeur + doigt pour tous les Momo éveillés ---------- */
  const awake = [];
  let px = -1, py = -1;
  addEventListener('pointermove', (e) => { px = e.clientX; py = e.clientY; }, { passive: true });
  const onTouch = (e) => { const t = e.touches && e.touches[0]; if (t) { px = t.clientX; py = t.clientY; } };
  addEventListener('touchstart', onTouch, { passive: true });
  addEventListener('touchmove', onTouch, { passive: true });
  let ticking = false;
  const tick = () => {
    for (const m of awake) {
      if (px >= 0) {
        const r = m.el.getBoundingClientRect();
        if (r.width > 0) {
          const dx = (px - (r.left + r.width / 2)) / innerWidth, dy = (py - (r.top + r.height * .4)) / innerHeight;
          m.tx = Math.max(-1, Math.min(1, dx * 2)) * 6; m.ty = Math.max(-1, Math.min(1, dy * 2)) * 4;
        }
      }
      m.cx += (m.tx - m.cx) * .1; m.cy += (m.ty - m.cy) * .1;
      const t = `translate(${m.cx.toFixed(2)} ${m.cy.toFixed(2)})`;
      for (const n of m.eyes) n.setAttribute('transform', t);
    }
    if (awake.length) requestAnimationFrame(tick); else ticking = false;
  };
  const watch = (el) => {
    if (awake.some((m) => m.el === el)) return;
    awake.push({ el, eyes: el.querySelectorAll('.momo__eye, .momo__hl'), tx: 0, ty: 0, cx: 0, cy: 0 });
    if (!ticking) { ticking = true; requestAnimationFrame(tick); }
  };
  const unwatch = (el) => { const i = awake.findIndex((m) => m.el === el); if (i >= 0) awake.splice(i, 1); };

  /* ---------- comportements ---------- */
  const isAsleep = (el) => el.classList.contains('momo--sleepy') || el.classList.contains('momo--still');
  const yawn = (el) => {
    if (reduce || !el.isConnected || el.classList.contains('is-yawn')) return;
    el.classList.add('is-yawn'); setTimeout(() => el.classList.remove('is-yawn'), 1600);
  };
  const boing = (el) => {
    if (reduce) return;
    el.classList.remove('is-boing'); void el.offsetWidth; el.classList.add('is-boing');
    setTimeout(() => el.classList.remove('is-boing'), 700);
    for (let i = 0; i < 6; i++) {
      const w = document.createElement('span'); w.innerHTML = STAR; const s = w.firstChild;
      const a = (i / 6) * Math.PI * 2 + Math.random() * .5, d = 40 + Math.random() * 40;
      s.style.setProperty('--dx', (Math.cos(a) * d).toFixed(0) + 'px'); s.style.setProperty('--dy', (Math.sin(a) * d - 20).toFixed(0) + 'px');
      s.style.setProperty('--dur', (.7 + Math.random() * .5).toFixed(2) + 's');
      el.appendChild(s); setTimeout(() => s.remove(), 1300);
    }
    if (!isAsleep(el) && Math.random() < .3) yawn(el);
  };
  const setState = (el, state) => {
    if (!STATES.includes(state)) state = 'awake';
    for (const s of STATES) el.classList.remove('momo--' + s);
    el.classList.add('momo--' + state);
    el.dataset.momo = state;
    const zz = el.querySelector('.momo__zz');
    if (state === 'sleepy') { if (!zz) el.insertAdjacentHTML('beforeend', '<div class="momo__zz"><span>z</span><span>z</span><span>z</span></div>'); unwatch(el); }
    else { if (zz) zz.remove(); if (state !== 'still' && !reduce) watch(el); }
    if (state !== 'sleepy') { el.classList.remove('is-lying'); (el._dozeT || []).forEach(clearTimeout); el._dozeT = []; }
  };
  /* s'endort pour de vrai : bâille → s'allonge → zzz */
  const doze = (el) => {
    (el._dozeT || []).forEach(clearTimeout);
    if (reduce) { setState(el, 'sleepy'); el.classList.add('is-lying'); return; }
    yawn(el);
    el._dozeT = [
      setTimeout(() => { el.classList.add('is-lying'); }, 1500),
      setTimeout(() => { setState(el, 'sleepy'); el.classList.add('is-lying'); }, 2600),
    ];
  };

  /* ---------- entrée en scène (IO) ---------- */
  let io = null;
  if (!reduce && 'IntersectionObserver' in window) {
    io = new IntersectionObserver((entries) => {
      for (const en of entries) {
        if (!en.isIntersecting) continue;
        const el = en.target; io.unobserve(el);
        const r = el.getBoundingClientRect();
        const fromRight = r.left + r.width / 2 > innerWidth / 2;
        el.style.setProperty('--from', (fromRight ? 70 : -70) + 'px');
        el.style.setProperty('--rot', (fromRight ? 10 : -10) + 'deg');
        el.classList.add('is-enter');
        setTimeout(() => { el.classList.remove('is-enter'); el.style.removeProperty('--from'); el.style.removeProperty('--rot'); }, 1200);
        if (el.dataset.momo === 'dozing') setTimeout(() => doze(el), 900);
      }
    }, { threshold: .4 });
  }

  const mount = (el) => {
    const state = el.dataset.momo || 'awake';
    const size = el.dataset.size || 120;
    el.classList.add('momo', 'is-mounted');
    el.style.setProperty('--momo-size', size + 'px');
    const shadow = document.createElement('div'); shadow.className = 'momo__shadow';
    el.prepend(shadow);
    const rig = document.createElement('div'); rig.className = 'momo__rig';
    const body = el.querySelector('.momo__body');
    if (body) rig.appendChild(body);
    rig.insertAdjacentHTML('beforeend', FACE);
    el.appendChild(rig);
    setState(el, state === 'dozing' ? 'awake' : state);
    if (state === 'dozing') { el.dataset.momo = 'dozing'; el.classList.add('momo--dozing'); if (reduce || !io) doze(el); }
    el.addEventListener('pointerdown', () => boing(el), { passive: true });
    if (reduce || state === 'still') return;
    if (io) io.observe(el);
    const blink = () => { if (!el.isConnected) return; if (!isAsleep(el)) { el.classList.add('is-blink'); setTimeout(() => el.classList.remove('is-blink'), 260); } setTimeout(blink, 2200 + Math.random() * 3200); };
    setTimeout(blink, 1200 + Math.random() * 1500);
    const autoYawn = () => { if (!el.isConnected) return; if (!isAsleep(el)) yawn(el); setTimeout(autoYawn, 9000 + Math.random() * 9000); };
    setTimeout(autoYawn, 5000 + Math.random() * 6000);
  };
  const init = () => document.querySelectorAll('[data-momo]:not(.is-mounted)').forEach(mount);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('mf:momo:mount', init);
  window.MomoMount = init;
  window.Momo = { mount: init, set: setState, yawn, boing, doze };
})();
